/* global clearInterval, console, CustomFunctions, setInterval */

import { BatchData} from "./corpo.model";
import { clearAllFormattedCells, clearFormatting, parseFormatType } from "./utils";
import { getTokenFromStorage, showSessionExpiredPopup } from "../taskpane/utils";
import { CustomExcelError } from "./custom.error";
import { pushQueue } from "./batch";
import { clearBatchCache } from "./services";
import { pendingFormats } from "./shared-state";


/**
 * Collect the data exactly as presented in the financial statements.
 * 
 * @customfunction Corporatings_xbrlcalculation XBRLCalculation
 * @cancelable
 * @requiresAddress
 * 
 * @param Issuer the name of the issuer to retrieve the metric for
 * @param Metric the name of the metric to retrieve
 * @param Year the year for which to retrieve the metric
 * @param format the output format: "M" (default) for millions, "B" for billions, "T" for thousands and "G" for standard format
 * 
 * 
 * @returns - the numeric value of the metric for the specified issuer and year
 */
export async function xbrlcalculation(Issuer: string, Metric: string, Year: number, format: FormatType = null, invocation: CustomFunctions.Invocation): Promise<number> {
    await checkRefreshFlag();
    const url = "/api/excel-addin/corpo-cube-issuer-batch";
    return geData(url, Issuer, Metric, Year, parseFormatType(format) as FormatType, invocation);

}


/**
 * Collect the data including the adjustments made by Corporatings in order to ensure consistent and meaningful comparability.
 * 
 * @customfunction Corporatings_asreported Asreported
 * @cancelable
 * @requiresAddress
 * 
 * @param Issuer the name of the issuer to retrieve the metric for
 * @param Metric the name of the metric to retrieve
 * @param Year the year for which to retrieve the metric
 * @param format the output format: "M" (default) for millions, "B" for billions, "T" for thousands and "G" for standard format
 * 
 * @returns - the numeric value of the metric for the specified issuer and year
 */
export async function asreported(Issuer: string, Metric: string, Year: number, format: FormatType = null, invocation: CustomFunctions.Invocation): Promise<number> {
    await checkRefreshFlag();
    const url = "/api/excel-addin/cube-issuer-batch";
    return geData(url, Issuer, Metric, Year, parseFormatType(format) as FormatType, invocation);
}


/**
 * Collect the data exactly as presented in the financial statements.
 * 
 * @customfunction Corporatings_standardized Standardized
 * @cancelable
 * @requiresAddress
 * 
 * @param Issuer the name of the issuer to retrieve the metric for
 * @param Metric the name of the metric to retrieve
 * @param Year the year for which to retrieve the metric
 * @param format the output format: "M" (default) for millions, "B" for billions, "T" for thousands and "G" for standard format
 * 
 * 
 * @returns - the numeric value of the metric for the specified issuer and year
 */
export async function standardized(Issuer: string, Metric: string, Year: number, format: FormatType = null, invocation: CustomFunctions.Invocation): Promise<number> {
    await checkRefreshFlag();
    const url = "/api/excel-addin/caculation-cube-issuer-batch";
    return geData(url, Issuer, Metric, Year, parseFormatType(format) as FormatType, invocation);

}


async function checkRefreshFlag() {
  const flag = await OfficeRuntime.storage.getItem("refresh_flag");
  if (flag !== "true") return;

  await clearAllFormattedCells();
  clearBatchCache();

  await OfficeRuntime.storage.removeItem("refresh_flag");
}


async function geData(url: string, Issuer: string, Metric: string, Year: number, format: FormatType, invocation: CustomFunctions.Invocation): Promise<number> {
    const token = getTokenFromStorage();

    if (!token) {
        await clearFormatting(invocation.address);
        showSessionExpiredPopup(
            "Not authenticated",
            "Your are not connected <br>Please login to access this data.",
            "Connect"
        );
        return Promise.reject(
            new CustomFunctions.Error(CustomFunctions.ErrorCode.notAvailable, "NOT CONNECTED")
        );
    }

    return pushQueue(url, Issuer, Metric, Year, parseFormatType(format) as FormatType, invocation)
        .then((data: BatchData) => {
            pendingFormats.set(
                invocation.address,
                {
                    link: data.link,
                    numericValue: data.numericValue,
                    unit: data.unit,
                    displayValue: data.displayValue,
                    format
                }
            );
            return data.numericValue;
        })
        .catch(async (error : CustomExcelError) => {
            await clearFormatting(invocation.address);
            throw new CustomFunctions.Error(
                error?.excelCode ?? CustomFunctions.ErrorCode.notAvailable,
                error?.message ?? "Data not available"
            );
        }
    );

}



/******************************************************************************************************/
/* ATTENTION : ENUM NON DEPLACABLE.
/******************************************************************************************************/

/**
 * Choose the monetary format
 * @customenum {string} 
 */
enum FormatType {
    /** Format en milliers */
    THOUSAND = "K",
    /** Format en millions */
    MILLION = "M",
    /** Format en milliards */
    BILLION = "B",
    /** Format standard */
    GENERAL = "G"
}



CustomFunctions.associate("CORPORATINGS_XBRLCALCULATION", xbrlcalculation);
CustomFunctions.associate("CORPORATINGS_ASREPORTED", asreported);
CustomFunctions.associate("CORPORATINGS_STANDARDIZED", standardized);